import { useEffect, useMemo, useRef, useState } from "react";

const getNumericValue = (value) => {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string") {
    const normalized = Number(value.replace(/[^0-9.-]+/g, ""));
    return Number.isFinite(normalized) ? normalized : null;
  }
  return null;
};

const prefersReducedMotion = () => {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
};

export const AnimatedNumber = ({
  value,
  decimals = 0,
  locale = "de-DE",
  prefix = "",
  suffix = "",
  fallback = "—",
  animate = true,
  className,
  dataTestId,
}) => {
  const targetValue = useMemo(() => getNumericValue(value), [value]);
  const [displayValue, setDisplayValue] = useState(targetValue);
  const previousValueRef = useRef(targetValue);

  useEffect(() => {
    if (targetValue === null) {
      previousValueRef.current = null;
      setDisplayValue(null);
      return undefined;
    }

    const previousValue = previousValueRef.current;
    if (!animate || prefersReducedMotion() || previousValue === null || previousValue === targetValue) {
      previousValueRef.current = targetValue;
      setDisplayValue(targetValue);
      return undefined;
    }

    const startTime = performance.now();
    const duration = 420;
    const delta = targetValue - previousValue;
    let frame = null;

    const tick = (time) => {
      const progress = Math.min(1, (time - startTime) / duration);
      const eased = 1 - ((1 - progress) ** 3);
      setDisplayValue(previousValue + (delta * eased));
      if (progress < 1) {
        frame = window.requestAnimationFrame(tick);
        return;
      }
      previousValueRef.current = targetValue;
      setDisplayValue(targetValue);
    };

    frame = window.requestAnimationFrame(tick);
    return () => {
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, [animate, targetValue]);

  if (targetValue === null || displayValue === null) {
    return <span className={className} data-testid={dataTestId}>{fallback}</span>;
  }

  return (
    <span className={className} data-testid={dataTestId}>
      {prefix}
      {new Intl.NumberFormat(locale, {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      }).format(displayValue)}
      {suffix}
    </span>
  );
};